import React, { useState, useEffect } from 'react';
import { Copy, X } from 'lucide-react';

interface CloneTemplateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (name: string, key: string) => Promise<void>;
    sourceName: string;
    sourceKey: string;
}

export const CloneTemplateModal: React.FC<CloneTemplateModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    sourceName,
    sourceKey,
}) => {
    const [name, setName] = useState('');
    const [key, setKey] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setName(`${sourceName} (Copy)`);
            setKey(`${sourceKey}_copy`);
        }
    }, [isOpen, sourceName, sourceKey]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim() || !key.trim()) return;
        setIsSubmitting(true);
        try {
            await onConfirm(name.trim(), key.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'));
            onClose();
        } catch (err) {
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-3 text-(--primary)">
                    <div className="p-2.5 bg-sky-50 dark:bg-sky-950/50 rounded-lg">
                        <Copy className="w-6 h-6 text-(--primary)" />
                    </div>
                    <h3 className="text-lg font-semibold dark:text-white">Clone Email Template</h3>
                </div>

                <p className="text-sm text-slate-600 dark:text-slate-400">
                    Create a duplicate of <strong className="text-slate-800 dark:text-slate-200">{sourceName}</strong>. Specify a new display name and unique template key.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase">
                            New Template Name *
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary)/50 text-slate-900 dark:text-white"
                            placeholder="Enter template name..."
                            required
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase">
                            New Key Slug *
                        </label>
                        <input
                            type="text"
                            value={key}
                            onChange={(e) => setKey(e.target.value)}
                            className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-955 border border-slate-300 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-(--primary)/50 text-slate-900 dark:text-white font-mono"
                            placeholder="e.g. welcome_user_copy"
                            required
                        />
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm font-bold bg-(--primary) hover:bg-(--primary-hover) text-white rounded-lg transition-colors shadow-sm disabled:opacity-50"
                            disabled={isSubmitting || !name.trim() || !key.trim()}
                        >
                            {isSubmitting ? 'Cloning...' : 'Confirm & Clone'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
